import React from 'react';
import PropTypes from 'prop-types';
import {Image} from '@learningblocks/shared-components/lib/Image';
import LoadingBars from '@learningblocks/exp-constants/lib/Images/LoadingBars.svg';
import './style.scss';

const BUTTON_TYPES = ['primary', 'secondary', 'start'];

/**
 * Button tag defines a clickable element.
 * Clicking on the element can be set to trigger some action.
 */
export function Button({
  ariaLabel,
  onClick,
  onKeyDown,
  onKeyUp,
  type,
  className,
  disabled,
  tabIndex,
  children,
  loading,
  onTouchStart,
  onTouchEnd,
  onMouseDown,
  onMouseUp,
  ...restProps
}) {
  const setClassName = () => ['button', type, className].join(' ');

  const renderButtonContent = () => {
    if (loading) {
      return (
        <Image
          className='button-loading'
          src={LoadingBars}
          alt='Loading bars'
          width='100%'
        />
      );
    } else {
      return children;
    }
  };

  return (
    <button
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      className={setClassName()}
      disabled={disabled}
      tabIndex={tabIndex}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      {...restProps}>
      {renderButtonContent()}
    </button>
  );
}

Button.propTypes = {
  /**
   * String value to be set as aria-label
   */
  ariaLabel: PropTypes.string,
  /**
   * Set what text you want to display on your button here.
   */
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.element,
  ]).isRequired,
  /**
   * Add your custom style class for the component here
   */
  className: PropTypes.string,
  /**
   * Control if you button is enabled or disabled
   */
  disabled: PropTypes.bool,
  /**
   * To display the loading state on button
   */
  loading: PropTypes.bool,
  /**
   * Add the click function handler here
   */
  // onClick: PropTypes.func.isRequired,
  /**
   * Number value to be set as tabIndex
   */
  tabIndex: PropTypes.number,
  /**
   * Prop sets the type of button ['primary','secondary','start']
   */
  type: PropTypes.oneOf(BUTTON_TYPES),
};

Button.defaultProps = {
  children: BUTTON_TYPES[0],
  className: '',
  disabled: false,
  loading: false,
  onClick: undefined,
  type: BUTTON_TYPES[0],
};
